import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useRecordingContext } from '../../../context/RecordingContext';
import {
  useCreateDocumentMutation,
  useLazyGetEncounterQuery,
  useUpdateEncounterMutation,
} from '../store/api';
import { selectActiveWorkflowEncounterId } from '../store/selectors';
import { clearActiveWorkflowEncounter } from '../store/workflowSlice';

/**
 * Navigation guard for recording workflow protection
 *
 * Implements the constraint:
 * - Allow navigation to completed encounters while recording (reference material)
 * - Block navigation to different pending encounters while recording (show modal)
 *
 * @returns Guard function and status
 */
export function useEncounterWorkflowGuard() {
  const dispatch = useAppDispatch();
  const activeWorkflowEncounterId = useAppSelector(selectActiveWorkflowEncounterId) as string | null;
  const { enqueueSnackbar } = useSnackbar();

  // Get recording state and control from RecordingContext (source of truth)
  const {
    recordingStatus,
    initiateEndRecording,
    currentTranscriptionSessionId,
    elapsedTime,
    getRecordingTimes,
  } = useRecordingContext();
  const isRecording = recordingStatus.isRecording;

  // Lazy query for checking target encounter status on-demand
  const [fetchTargetEncounter] = useLazyGetEncounterQuery();

  // Mutations for saving recording as document
  const [createDocument] = useCreateDocumentMutation();
  const [updateEncounter] = useUpdateEncounterMutation();

  // Modal state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingConfirmCallback, setPendingConfirmCallback] = useState<(() => void) | null>(null);

  /**
   * Check if navigation to a specific encounter should be allowed
   *
   * @param targetEncounterId - The encounter ID user wants to navigate to
   * @returns Navigation decision
   */
  const checkNavigation = useCallback(
    async (
      targetEncounterId: string | null
    ): Promise<{ allowed: boolean; reason?: string; targetStatus?: string }> => {
      // No active workflow - allow
      if (!activeWorkflowEncounterId || !isRecording) {
        return { allowed: true };
      }

      // Navigating to same encounter - allow
      if (targetEncounterId === activeWorkflowEncounterId) {
        return { allowed: true };
      }

      // Navigating to /encounters/active (no specific ID) - allow
      if (!targetEncounterId) {
        return { allowed: true };
      }

      // Phase 3: Check target encounter status
      try {
        const { data: targetEncounter, error } = await fetchTargetEncounter({
          encounterId: targetEncounterId,
        });

        if (error || !targetEncounter) {
          console.error('[WorkflowGuard] Error fetching target encounter:', error);
          return { allowed: false, reason: 'error-fetching-encounter' };
        }

        // Allow navigation to completed/processing encounters (read-only)
        // These can be viewed for reference while recording
        if (targetEncounter.status === 'completed' || targetEncounter.status === 'processing') {
          return { allowed: true, targetStatus: targetEncounter.status };
        }

        // Block navigation to different pending (editable) encounters while recording
        // This prevents data loss or confusion about which encounter is being recorded
        if (targetEncounter.status === 'pending') {
          return {
            allowed: false,
            reason: 'cannot-navigate-to-different-pending-encounter',
            targetStatus: targetEncounter.status,
          };
        }

        // Unknown status - be permissive
        return { allowed: true, targetStatus: targetEncounter.status };
      } catch (error) {
        console.error('[WorkflowGuard] Error checking target encounter:', error);
        return { allowed: false, reason: 'error-checking-encounter' };
      }
    },
    [activeWorkflowEncounterId, isRecording, fetchTargetEncounter]
  );

  /**
   * Guard function to call before navigation
   * Shows modal if navigation should be blocked
   *
   * @param targetPath - Path user wants to navigate to
   * @param onConfirm - Callback if user confirms stopping recording
   * @returns Promise<boolean> - true if navigation allowed, false if blocked
   */
  const guardNavigation = useCallback(
    async (targetPath: string, onConfirm?: () => void): Promise<boolean> => {
      // Extract encounter ID from path (handle both /encounters and /first-encounter routes)
      const match = targetPath.match(/\/(encounters|first-encounter)(\/([a-f0-9-]+|active))?/);
      const targetEncounterId = match?.[3] === 'active' ? null : match?.[3] ?? null;

      const check = await checkNavigation(targetEncounterId);

      if (check.allowed) {
        return true;
      }

      // Show modal instead of window.confirm
      setPendingConfirmCallback(() => onConfirm);
      setShowConfirmDialog(true);
      return false; // Navigation will be handled by modal confirmation
    },
    [checkNavigation]
  );

  /**
   * Handle modal confirmation - save recording and allow navigation
   */
  const handleConfirmNavigation = useCallback(async () => {
    setIsProcessing(true);
    try {
      // Capture origin encounter ID before any state changes
      const originEncounterId = activeWorkflowEncounterId;

      // Stop recording
      const success = await initiateEndRecording();
      if (!success) {
        enqueueSnackbar('Failed to stop recording', { variant: 'error' });
        setIsProcessing(false);
        return;
      }

      // If recording is too short (<5 sec), don't create a document
      if (elapsedTime < 5) {
        setShowConfirmDialog(false);
        setIsProcessing(false);
        // Call navigation callback BEFORE clearing workflow (keeps blocker in 'blocked' state)
        pendingConfirmCallback?.();
        // Clear workflow after navigation initiated
        dispatch(clearActiveWorkflowEncounter({ reason: 'manual' }));
        return;
      }

      // Create document to save recording work
      if (originEncounterId && currentTranscriptionSessionId) {
        // Fetch encounter to count existing recordings
        const { data: encounter } = await fetchTargetEncounter({
          encounterId: originEncounterId,
        });

        const existingRecordings =
          (encounter?.inputs || []).filter(doc => doc.type === 'recording').length;

        // Title logic based on duration (matches stop button behavior)
        const DICTATION_THRESHOLD = 60; // seconds
        const title =
          elapsedTime >= DICTATION_THRESHOLD
            ? 'Encounter Recording'
            : `Dictation ${existingRecordings + 1}`;

        // Create document (no need to wait for readyToSubmit - that's only for note generation)
        await createDocument({
          encounterId: originEncounterId,
          createDocumentRequest: {
            type: 'recording',
            title,
            content: 'Audio recording saved as context',
            transcription_session_id: currentTranscriptionSessionId,
          },
        }).unwrap();

        // Update encounter times if full recording (>= 60 seconds)
        if (elapsedTime >= DICTATION_THRESHOLD) {
          const recordingTimes = getRecordingTimes();
          if (recordingTimes) {
            await updateEncounter({
              encounterId: originEncounterId,
              encounterUpdate: {
                start_time: recordingTimes.startTime.toISOString(),
                end_time: recordingTimes.endTime.toISOString(),
              },
            }).unwrap();
          }
        }

        // Success feedback
        enqueueSnackbar(
          elapsedTime >= DICTATION_THRESHOLD ? 'Encounter recording saved' : 'Dictation saved',
          { variant: 'success' }
        );
      }

      // Close modal and trigger navigation BEFORE clearing workflow
      // This ensures blocker is still in 'blocked' state when proceed() is called
      setShowConfirmDialog(false);
      pendingConfirmCallback?.();
      // Clear workflow after navigation initiated
      dispatch(clearActiveWorkflowEncounter({ reason: 'manual' }));
    } catch (error) {
      console.error('[WorkflowGuard] Error saving recording:', error);
      enqueueSnackbar('Failed to save recording', { variant: 'error' });
      setShowConfirmDialog(false);
      // Even if saving fails, clear workflow (recording stopped)
      dispatch(clearActiveWorkflowEncounter({ reason: 'manual' }));
    } finally {
      setIsProcessing(false);
      setPendingConfirmCallback(null);
    }
  }, [
    activeWorkflowEncounterId,
    initiateEndRecording,
    enqueueSnackbar,
    elapsedTime,
    dispatch,
    currentTranscriptionSessionId,
    fetchTargetEncounter,
    createDocument,
    updateEncounter,
    getRecordingTimes,
    pendingConfirmCallback,
  ]);

  /**
   * Handle modal cancellation - stay on current page
   */
  const handleCancelNavigation = useCallback(() => {
    setShowConfirmDialog(false);
    setPendingConfirmCallback(null);
  }, []);

  return {
    /** Check if navigation is allowed (no side effects) */
    checkNavigation,

    /** Guard navigation (shows modal if blocked) */
    guardNavigation,

    /** Whether user is currently recording */
    isRecording: isRecording,

    /** Active workflow encounter ID */
    activeWorkflowEncounterId: activeWorkflowEncounterId,

    /** Modal state and handlers */
    showConfirmDialog,
    isProcessing,
    handleConfirmNavigation,
    handleCancelNavigation,
  };
}
